import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
const WARNING_BEFORE = 60 * 1000; // Show warning 1 minute before logout

export default function IdleTimeout({ isLoggedIn }) {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const countdownRef = useRef(null);
  const initializedRef = useRef(false);

  const handleLogout = useCallback(() => {
    // Clear all timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    // Clear auth data
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    
    setShowWarning(false);
    
    // Navigate to login
    navigate('/', { replace: true });
  }, [navigate]);

  const resetTimer = useCallback(() => {
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    setShowWarning(false);
    setCountdown(60);

    if (!isLoggedIn) return;

    // Set warning timeout (14 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(60);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, IDLE_TIMEOUT - WARNING_BEFORE);

    // Set final logout timeout (15 minutes)
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, IDLE_TIMEOUT);
  }, [isLoggedIn, handleLogout]);

  const handleStayLoggedIn = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!isLoggedIn) {
      // Clear timeouts when logged out
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      initializedRef.current = false;
      return;
    }

    // Events that reset the idle timer
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle reset to avoid excessive calls
    let lastReset = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset > 1000) { // Only reset once per second max
        lastReset = now;
        resetTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Initial timer setup - only once
    if (!initializedRef.current) {
      initializedRef.current = true;
      // Use setTimeout to avoid setState during render
      setTimeout(() => resetTimer(), 0);
    }

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isLoggedIn, resetTimer]);

  if (!isLoggedIn) return null;

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" data-testid="idle-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle size={24} />
            Session Timeout Warning
          </DialogTitle>
          <DialogDescription>
            You have been inactive for a while. For your security, you will be automatically logged out.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-6 text-center">
          <div className="text-5xl font-bold text-amber-600 mb-2" data-testid="countdown-timer">
            {countdown}
          </div>
          <p className="text-sm text-muted-foreground">seconds until logout</p>
        </div>
        
        <DialogFooter className="flex gap-2 sm:justify-center">
          <Button 
            variant="outline" 
            onClick={handleLogout}
            data-testid="logout-now-btn"
          >
            Logout Now
          </Button>
          <Button 
            onClick={handleStayLoggedIn}
            data-testid="stay-logged-in-btn"
          >
            Stay Logged In
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
